import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import RewardsService from "../../services/reward";
import Utils from "../../utility";
import { useDispatch, useSelector } from "react-redux";
import { hideLoader, removeUser, showLoader } from "../../slices/userSlice";
import {
  successMessageConstant,
  failureMessageConstant,
  couponTypes,
  SampleCSV,
  preventInput,
  keyConstants
} from "../../constants";
import CSVReader from "react-csv-reader";
import SelectDate from "../../common/components/selectDate";
import DropDown from "../../common/components/dropdown";
import PropTypes from "prop-types";

const AddCoupon = ({
  handleClose,
  item,
  isEdit,
  fetchCouponData,
  page,
  itemsPerPage
}) => {
  const csvReaderRef = useRef(null);
  // COUPON CODE TYPE
  const allCouponTypes = [
    {
      name: "Unique Coupon Code",
      value: "UNIQUE"
    },
    {
      name: "Global Coupon Code",
      value: "GLOBAL"
    }
  ];
  const [couponType, setCouponType] = useState({
    name:
      item?.couponType === "Unique"
        ? allCouponTypes[0].name
        : allCouponTypes[1].name || couponTypes.GLOBAL.NAME,
    value: item?.couponType?.toUpperCase() || couponTypes.GLOBAL.NAME
  });
  const [discountCodesError, setDiscountCodesError] = useState("");
  const [csvKey, setCSVKey] = useState(0);
  const [expiryDate, setExpiryDate] = useState(null);
  const [expiryDateErr, setExpiryDateErr] = useState("");
  const [calenderOpen, setCalenderOpen] = useState(false);
  const [showOption, setShowOption] = useState(false);
  const user = useSelector((state) => state.user);
  const calendarDropDownRef = useRef(null);
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
    reset,
    getValues
  } = useForm();
  useEffect(() => {
    reset();
  }, [couponType]);
  // FUNCTION TO ADD/UPDATE COUPON
  const { IS_REFRESH_TOKEN_LOADING } = keyConstants;
  useEffect(() => {
    const handleClickOutsideDropdown = (event) => {
      if (
        calendarDropDownRef.current &&
        !calendarDropDownRef.current.contains(event.target)
      ) {
        setCalenderOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideDropdown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideDropdown);
    };
  }, []);
  const addUpdateCoupon = async (formData) => {
    const tokenStatus = await Utils.checkTokenExpireOrNot();
    if (tokenStatus) {
      if (tokenStatus === IS_REFRESH_TOKEN_LOADING) return;
      dispatch(removeUser({}));
      return;
    }
    let disCountValue = formData.discountValue;
    if (Utils.isPurelyNumeric(disCountValue)) {
      disCountValue = parseInt(disCountValue);
      if (isNaN(disCountValue) || disCountValue < 1 || disCountValue > 100) {
        setError("discountValue", {
          message: failureMessageConstant.INVALID_DISCOUNT_VALUE
        });
        return;
      }
    }
    if (!expiryDate) {
      setExpiryDateErr(failureMessageConstant.EXPIRY_DATE_REQUIRED);
      return;
    }
    setExpiryDateErr("");
    try {
      dispatch(showLoader());
      let discountCodes;
      if (typeof formData.discountCode === "string") {
        discountCodes = formData.discountCode
          .split(",")
          .map((code) => code.trim())
          .filter((code) => code !== "");
      } else if (Array.isArray(formData.discountCode)) {
        discountCodes = formData.discountCode.filter((code) => code !== "");
      } else {
        discountCodes = [];
      }

      if (discountCodesError) {
        return;
      }

      const rewardsService = new RewardsService();
      if (isEdit) {
        await rewardsService.updateCoupon({
          couponId: item?.id,
          discountCode: discountCodes,
          expiryDate: expiryDate
        });
      } else {
        await rewardsService.addCoupon({
          ...formData,
          couponType: couponType?.value,
          businessId: user?._id,
          discountCode: discountCodes,
          expiryDate: expiryDate
        });
      }
      handleClose();
      const successMessage = isEdit
        ? successMessageConstant.COUPON_UPDATED_SUCCESSFULLY
        : successMessageConstant.COUPON_ADDED_SUCCESSFULLY;
      await fetchCouponData((page - 1) * itemsPerPage, itemsPerPage);
      Utils.successToastMessage(successMessage);
    } catch (error) {
      if (
        error?.message === failureMessageConstant.DUPLICATE_COUPON ||
        error?.response?.data?.message ===
          failureMessageConstant.DUPLICATE_COUPON
      )
        Utils.failureToastMessage(
          failureMessageConstant.DISCOUNT_CODE_ALREADY_EXISTS
        );
      else Utils.failureToastMessage(error?.message);
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    setValue("discountValue", item?.discountValue);
    setExpiryDate(item?.expiryDate);
    setExpiryDateErr("");
  }, [item]);

  const handleInputChange = (fieldName, e) => {
    let value = e.target.value;
    if (fieldName === "discountValue") {
      if (errors.discountValue) {
        setError("discountValue", {
          message: ""
        });
      }
    }
    if (fieldName === "quantity") {
      value = parseInt(value);
      if (isNaN(value) || value < 1 || value > 9999) {
        value = "";
      }
    }
    setValue(fieldName, value);
  };
  const handleInputKeyDown = (fieldName, e) => {
    if (fieldName === "quantity") {
      if (preventInput.DISCOUNT_QUANTITY.includes(e.key)) {
        e.preventDefault();
      }
    }
  };

  const isDuplicate = (data) => {
    const uniqueCodes = [];
    const duplicateCodes = [];
    const uniqueCodesSet = new Set();

    if (item?.discountCodes) {
      item.discountCodes.forEach((code) => {
        if (!uniqueCodesSet.has(code)) {
          uniqueCodesSet.add(code);
          uniqueCodes.push(code);
        }
      });
    }

    data.forEach((code) => {
      if (!uniqueCodesSet.has(code)) {
        uniqueCodesSet.add(code);
        uniqueCodes.push(code);
      } else {
        duplicateCodes.push(code);
      }
    });

    return { uniqueCodes, duplicateCodes };
  };

  // FUNCTION TO UPLOAD & READ CSV WITH ALL VALIDATIONS
  const uploadAndReadCSV = (data) => {
    const invalidCodeLength = data.some((row) => {
      const discountCode = row[0]?.trim();
      const codeLength = discountCode?.length;
      return codeLength < 6 || codeLength > 15;
    });

    if (invalidCodeLength || data?.length === 0) {
      Utils.failureToastMessage(
        failureMessageConstant.CSV_IS_NOT_MEETING_CRITERIA
      );
      return;
    }
    const discountCodes = data.map((row) => row[0]);
    // Check for duplicate entires and throw error
    const { duplicateCodes } = isDuplicate(discountCodes);
    if (duplicateCodes.length > 0) {
      Utils.failureToastMessage(
        failureMessageConstant.DISCOUNT_CODE_MUST_BE_UNIQUE
      );
      return;
    }
    // Get the current value of "discountCode" field using getValues method of useForm
    const currentDiscountCodes = getValues("discountCode");

    // Combine previous discount codes with new ones
    const combinedDiscountCodes = currentDiscountCodes.concat(discountCodes);
    setValue("discountCode", combinedDiscountCodes);
  };

  const handlleUploadCSVClick = () => {
    csvReaderRef.current.click();
  };
  // FUNCTION TO HANDLE FILE UPLOAD AND UPDATE CSV KEY STATE
  const handleFileLoaded = (data) => {
    uploadAndReadCSV(data);
    // Incrementing the key to force re-render of CSVReader
    setCSVKey((prevKey) => prevKey + 1);
  };
  // FUNCTION TO VALIDATE AND FILTER CODES
  const validateAndFilterCodes = (value) => {
    if (!value) {
      setDiscountCodesError(failureMessageConstant.DISCOUNT_CODES_REQUIRED);
      return;
    }
    const codes = value.split(",").map((code) => code.trim());
    const invalidCodes = codes.filter(
      (code) => code.length < 6 || code.length > 15
    );

    // Check for Duplicate Entries
    const { uniqueCodes, duplicateCodes } = isDuplicate(codes);
    if (invalidCodes.length > 0) {
      setDiscountCodesError(
        failureMessageConstant.DISCOUNT_CODE_MUST_BE_BETWEEN_6_AND_15_CHAR
      );
    } else if (duplicateCodes.length > 0) {
      setDiscountCodesError(
        failureMessageConstant.DISCOUNT_CODE_MUST_BE_UNIQUE
      );
    } else {
      setDiscountCodesError("");
    }
    return uniqueCodes.filter((code) => code.length >= 6 && code.length <= 15);
  };
  // FUNCTION TO VALIDATE QUANTITY
  const validateQuantity = (value) => {
    const parsedValue = parseInt(value);
    if (parsedValue === 0) {
      return failureMessageConstant.QUANTITY_SHOULD_BE_GREATER_THAN_ZERO;
    } else if (parsedValue > 9999) {
      return failureMessageConstant.QUANTITY_SHOULD_BE_LESS_THAN_OR_EQUAL_9999;
    }
  };

  // FUNCTION TO DOWNLOAD SAMPLE CSV FILE
  const downloadCSV = () => {
    // Create a blob from the CSV data
    const blob = new Blob([SampleCSV], { type: "text/csv" });

    // Create a link element
    const link = document.createElement("a");

    // Set the href attribute to the object URL of the blob
    link.href = window.URL.createObjectURL(blob);

    // Set the download attribute to specify the filename
    link.download = "Sample Coupons.csv";

    // Simulate a click on the link element to trigger the download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
  };

  return (
    <div className="fixed left-0 top-0 z-10 flex size-full items-start justify-center border-grey-DDDFDE bg-black-00000050 px-3">
      <form
        onSubmit={handleSubmit(addUpdateCoupon)}
        className="relative mt-9 w-full max-w-457px rounded-lg bg-white px-3 pt-23px md:px-6"
      >
        <div className="flex flex-col">
          <div className="font-MontserratSemiBold text-ft22-26.82-0.4 text-black-20">
            {isEdit ? "Edit Coupon" : "Add Coupon"}
          </div>
          <div className="mt-4.5 font-MontserratMedium text-ft15-26.25 text-black-30">
            Coupon Code Type
          </div>
          <DropDown
            value={couponType}
            setValue={setCouponType}
            data={allCouponTypes}
            placeHolder={"Select Coupon Code Type"}
            customizeDropDown={{
              maxWidth: "100%",
              height: "h-49px"
            }}
            showOption={showOption}
            setShowOption={setShowOption}
            isEdit={isEdit}
          />
          <div className="mt-5.5 font-MontserratMedium text-ft15-26.25 text-black-30">
            Discount Value
          </div>
          <input
            type="text"
            className={`h-49px w-full rounded border ${errors.discountValue?.message ? "border-red-10" : "border-grey-D9D9D9"}  px-13px py-10.72px font-MontserratMedium placeholder:text-grey-20 focus:outline-none`}
            placeholder="Enter discount"
            {...register("discountValue", {
              required: failureMessageConstant.DISCOUNT_VALUE_REQUIRED,
              maxLength: {
                value: 20,
                message: failureMessageConstant.DISCOUNT_VALUE_TOO_LONG
              }
            })}
            onChange={(e) => handleInputChange("discountValue", e)}
            onKeyDown={(e) => handleInputKeyDown("discountValue", e)}
            disabled={isEdit}
          />
          {errors.discountValue && (
            <p className="mt-2 font-MontserratRegular text-ft13-15.85-0.26 text-red-10">
              {errors.discountValue.message}
            </p>
          )}
          {couponType?.value === couponTypes.GLOBAL.NAME && (
            <div>
              <div className="mt-5 font-MontserratMedium text-ft15-26.25 text-black-30">
                Discount Code
              </div>
              <input
                type="text"
                className={`h-11.5 w-full rounded border ${errors.discountCode ? "border-red-10" : "border-grey-D9D9D9"} px-13px py-10.72px font-MontserratMedium placeholder:text-grey-20 focus:outline-none`}
                placeholder="Enter coupon code"
                {...register("discountCode", {
                  required: failureMessageConstant.DISCOUNT_CODE_REQUIRED,
                  minLength: {
                    value: 6,
                    message:
                      failureMessageConstant.DISCOUNT_CODE_MUST_BE_BETWEEN_6_AND_15_CHAR
                  },
                  maxLength: {
                    value: 15,
                    message:
                      failureMessageConstant.DISCOUNT_CODE_MUST_BE_BETWEEN_6_AND_15_CHAR
                  }
                })}
              />
              {errors.discountCode && (
                <p className="mt-2 font-MontserratRegular text-ft13-15.85-0.26 text-red-10">
                  {errors.discountCode.message}
                </p>
              )}
              <div className="mt-21px font-MontserratMedium text-ft15-26.25 text-black-30">
                Quantity
              </div>
              <input
                type="number"
                className={`h-11.5 w-full rounded border ${errors.quantity ? "border-red-10" : "border-grey-D9D9D9"} px-13px py-10.72px font-MontserratMedium placeholder:text-grey-20 focus:outline-none`}
                placeholder="Enter coupon count"
                {...register("quantity", {
                  required: failureMessageConstant.QUANTITY_REQUIRED,
                  validate: validateQuantity
                })}
                onChange={(e) => handleInputChange("quantity", e)}
                onKeyDown={(e) => handleInputKeyDown("quantity", e)}
              />
              {errors.quantity && (
                <p className="mt-2 font-MontserratRegular text-ft13-15.85-0.26 text-red-10">
                  {errors.quantity.message}
                </p>
              )}
            </div>
          )}
          {couponType?.value === couponTypes.UNIQUE.NAME && (
            <div>
              <div className="flex flex-row items-baseline justify-between ">
                <div className="mt-5 font-MontserratMedium text-ft15-26.25 text-black-30">
                  Discount Codes
                </div>
                <div>
                  <label
                    htmlFor="csvUploadInput"
                    className="mt-6 cursor-pointer font-MontserratMedium text-ft13-15.85-0.26 text-purple-6C36B0"
                    onClick={handlleUploadCSVClick}
                  >
                    Upload via CSV
                  </label>
                  <div className="hidden">
                    <CSVReader
                      id="csvUploadInput"
                      key={csvKey}
                      ref={csvReaderRef}
                      onFileLoaded={handleFileLoaded}
                    />
                  </div>
                </div>
              </div>
              <textarea
                type="text"
                className={`h-32 w-full resize-none rounded border ${errors?.discountCode ? "border-red-10" : "border-grey-D9D9D9"} px-13px py-10.72px focus:outline-none `}
                {...register("discountCode", {
                  required: failureMessageConstant.DISCOUNT_CODES_REQUIRED
                })}
                onChange={(e) => validateAndFilterCodes(e.target.value)}
              />
              <p className="font-MontserratRegular text-ft13-15.85-0.26 text-grey-767676">
                Need a sample CSV format?{" "}
                <span
                  onClick={downloadCSV}
                  className="cursor-pointer text-blue-726FFF "
                >
                  Download
                </span>
              </p>
              {(errors?.discountCode && (
                <p className="mt-2 font-MontserratRegular text-ft13-15.85-0.26 text-red-10">
                  {errors.discountCode.message}
                </p>
              )) ||
                (discountCodesError && (
                  <p className="mt-2 font-MontserratRegular text-ft13-15.85-0.26 text-red-10">
                    {discountCodesError}
                  </p>
                ))}
            </div>
          )}
          <div className="mt-21px font-MontserratMedium text-ft15-26.25 text-black-30">
            Coupon Expiry Date
          </div>
          <SelectDate
            date={expiryDate}
            setDate={setExpiryDate}
            calenderOpen={calenderOpen}
            setCalenderOpen={setCalenderOpen}
            placeHolder={"Enter date"}
            expiryDateErr={expiryDateErr}
            calendarDropDownRef={calendarDropDownRef}
            isEdit={isEdit}
          />
          {expiryDateErr && (
            <p className="mt-2 font-MontserratRegular text-ft13-15.85-0.26 text-red-10">
              {expiryDateErr}
            </p>
          )}
          <div
            className={`flex justify-between gap-2 ${
              couponType?.value === couponTypes.GLOBAL.NAME
                ? "mb-8 mt-29px"
                : "my-5.5"
            }`}
          >
            <div>
              <button
                type="button"
                onClick={handleClose}
                className="h-12 w-35 rounded border border-grey-D9D9D9 md:w-183px"
              >
                <span className="font-MontserratMedium text-ft15-26.25 text-black-000000">
                  Cancel
                </span>
              </button>
            </div>
            <div>
              <button
                type="submit"
                className="h-12 w-35 rounded border border-grey-D9D9D9 bg-purple-6C36B0 text-white md:w-183px"
              >
                <span className="font-MontserratMedium text-ft15-26.25">
                  {isEdit ? "Update Coupons" : "Add Coupons"}
                </span>
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
AddCoupon.propTypes = {
  handleClose: PropTypes.func.isRequired,
  item: PropTypes.object, // Optional
  isEdit: PropTypes.bool.isRequired,
  fetchCouponData: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired
};
export default AddCoupon;
